import type { IAuthRepository } from '@src/domain/users/repository';
import { RefreshTokenUseCase } from './refresh_token.usecase';
import { GetCurrentUserUseCase } from './get_current_user.usecase';
import { LogoutUseCase } from './logout.usecase';
import { AuthResponseMapper } from '../mappers/auth-response.mapper';
import type {
    SendOtpResponseDTO,
    VerifyOtpResponseDTO,
    OnboardingResponseDTO,
    UserResponseDTO,
    AuthTokensResponseDTO,
    WhatsAppLinkCodeResponseDTO,
} from '../dto/auth-response.dto';

/**
 * Auth Service
 * Central service for authentication operations
 */
export class AuthService {
    private readonly refreshTokenUseCase: RefreshTokenUseCase;
    private readonly getCurrentUserUseCase: GetCurrentUserUseCase;
    private readonly logoutUseCase: LogoutUseCase;

    constructor(private readonly authRepository: IAuthRepository) {
        this.refreshTokenUseCase = new RefreshTokenUseCase(authRepository);
        this.getCurrentUserUseCase = new GetCurrentUserUseCase(authRepository);
        this.logoutUseCase = new LogoutUseCase(authRepository);
    }

    /**
     * Send OTP to user's email for authentication
     */
    async sendOtp(email: string): Promise<SendOtpResponseDTO> {
        const result = await this.authRepository.sendOtp(email);
        return AuthResponseMapper.sendOtpResultToDTO(result);
    }

    /**
     * Verify OTP and authenticate user
     */
    async verifyOtp(email: string, code: string): Promise<VerifyOtpResponseDTO> {
        const result = await this.authRepository.verifyOtp(email, code);
        return AuthResponseMapper.verifyOtpResultToDTO(result);
    }

    /**
     * Set user's university during onboarding
     * Only allowed when profile_state is UNIVERSITY_REQUIRED
     */
    async setUniversity(universityId: string): Promise<OnboardingResponseDTO> {
        const result = await this.authRepository.setUniversity(universityId);
        return AuthResponseMapper.onboardingResultToDTO(result);
    }

    /**
     * Set user's name during onboarding
     * Only allowed when profile_state is NAME_REQUIRED
     */
    async setName(firstName: string, lastName: string, middleName?: string): Promise<OnboardingResponseDTO> {
        const result = await this.authRepository.setName(firstName, lastName, middleName);
        return AuthResponseMapper.onboardingResultToDTO(result);
    }

    async refreshToken(refreshToken: string): Promise<AuthTokensResponseDTO> {
        const result = await this.refreshTokenUseCase.execute(refreshToken);
        return AuthResponseMapper.tokensToDTO(result);
    }

    async getCurrentUser(): Promise<UserResponseDTO> {
        const result = await this.getCurrentUserUseCase.execute();
        return AuthResponseMapper.userToDTO(result);
    }

    async logout() {
        return this.logoutUseCase.execute();
    }

    /**
     * Generate a WhatsApp link code for account linking
     */
    async generateWhatsAppLinkCode(): Promise<WhatsAppLinkCodeResponseDTO> {
        const result = await this.authRepository.generateWhatsAppLinkCode();
        return AuthResponseMapper.whatsAppLinkCodeResultToDTO(result);
    }
}

